import { useState, useEffect, useMemo } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { ResidueStep, ResiduePayload } from "../forms/ResidueStep";
import { CalendarIcon, InfoIcon, ArrowLeft, ArrowRight, Check, RefreshCw, AlertCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Stepper, StepType } from "@/components/ui/stepper";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MaterialsStep, MaterialPayload } from "../forms/MaterialsStep";
import { ElectricityConsumptionStep, ElectricityPayload } from "../forms/ElectricityConsumptionStep";
import { FuelsStep, FuelPayload } from "../forms/FuelsStep";
import { WaterStep } from "../forms/WaterStep";
import { WaterPayload } from "@/types/water";
import { ConfidentialityDisclaimer } from "@/components/footprints/ConfidentialityDisclaimer";

interface AnnualConsumptionsStepProps {
  year: string;
  existingData?: any;
  onDataChange: (category: string, data: any) => void;
  formData: Record<string, any>;
  currentSubStep: number;
  onSubStepChange: (step: number) => void;
  onComplete?: () => void;
  onIncomplete?: () => void;
  onSaveDraft?: () => void;
  isSavingDraft?: boolean;
  hasUnsavedChanges?: boolean;
  lastSaved?: Date | null;
  onNavigateToNextMainStep?: () => void;
  companyType?: string;
}

export const AnnualConsumptionsStep = ({
  year,
  existingData,
  onDataChange,
  formData,
  currentSubStep,
  onSubStepChange,
  onComplete,
  onIncomplete,
  onSaveDraft,
  isSavingDraft,
  hasUnsavedChanges,
  lastSaved,
  onNavigateToNextMainStep,
  companyType,
}: AnnualConsumptionsStepProps) => {
  const { t } = useLanguage();

  // For mixed companies, keep separate state for extraction and transformation
  const [extractionData, setExtractionData] = useState<Record<string, any>>(() => {
    const initialData =
      companyType === "extraction_and_transformation"
        ? formData?.annualConsumptions?.extraction || {}
        : formData?.extractionAnnualConsumptions || {};

    return initialData;
  });

  const [transformationData, setTransformationData] = useState<Record<string, any>>(() => {
    const initialData =
      companyType === "extraction_and_transformation"
        ? formData?.annualConsumptions?.transformation || {}
        : formData?.transformationAnnualConsumptions || {};

    return initialData;
  });

  const [localData, setLocalData] = useState<Record<string, any>>(formData || {});

  // Helper function to check if data is meaningful (not empty)
  const hasValidData = (data: any): boolean => {
    if (!data) return false;

    // For arrays, check if it has actual data
    if (Array.isArray(data)) {
      return data.length > 0;
    }

    if (typeof data === "object") {
      // Check if object has meaningful content
      return (
        Object.keys(data).length > 0 &&
        Object.values(data).some((value) => {
          if (Array.isArray(value)) return value.length > 0;
          return Boolean(value);
        })
      );
    }
    return Boolean(data);
  };

  // Check if step is completed (has data)
  const isStepCompleted = (stepId: string): boolean => {
    if (companyType === "extraction_and_transformation") {
      // For mixed companies, check if either extraction or transformation has data
      const extractionHasData = hasValidData(extractionData[stepId]);
      const transformationHasData = hasValidData(transformationData[stepId]);
      return extractionHasData || transformationHasData;
    }

    return hasValidData(localData[stepId]);
  };

  useEffect(() => {
    if (existingData) {
      setLocalData(existingData);

      // For mixed companies, also update extraction and transformation data
      if (companyType === "extraction_and_transformation" && existingData?.annualConsumptions) {
        setExtractionData(existingData.annualConsumptions.extraction || {});
        setTransformationData(existingData.annualConsumptions.transformation || {});
      }
    }
  }, [existingData, companyType]);

  // Sync extraction and transformation data with formData for mixed companies
  useEffect(() => {
    if (companyType === "extraction_and_transformation" && formData?.annualConsumptions) {
      const extractionFromFormData = formData.annualConsumptions.extraction || {};
      const transformationFromFormData = formData.annualConsumptions.transformation || {};

      // Update state with form data
      setExtractionData(extractionFromFormData);
      setTransformationData(transformationFromFormData);
    }
  }, [formData?.annualConsumptions, companyType]);

  // Memoize steps array to avoid unnecessary re-renders and infinite loops
  const steps = useMemo<StepType[]>(
    () => [
      {
        id: "materials",
        title: t("footprints.categories.materials.title"),
        isOptional: false,
        isCompleted: isStepCompleted("materials"),
      },
      {
        id: "electricity",
        title: t("footprints.categories.electricity.title"),
        isOptional: false,
        isCompleted: isStepCompleted("electricity"),
      },
      {
        id: "fuels",
        title: t("footprints.categories.fuels.title"),
        isOptional: false,
        isCompleted: isStepCompleted("fuels"),
      },
      {
        id: "residue",
        title: t("footprints.categories.residue.title"),
        isOptional: false,
        isCompleted: isStepCompleted("residue"),
      },
      {
        id: "water",
        title: t("footprints.categories.water.title"),
        isOptional: false,
        isCompleted: isStepCompleted("water"),
      },
    ],
    [t, localData, extractionData, transformationData, companyType]
  );

  // mark completed if ALL required substeps have data
  useEffect(() => {
    // Check if ALL required substeps have meaningful data (to match Review step validation logic)
    const hasAllRequiredData = steps.every((step) => isStepCompleted(step.id));

    // Call the appropriate callback based on completion status
    if (hasAllRequiredData && onComplete) {
      onComplete();
    } else if (!hasAllRequiredData && onIncomplete) {
      onIncomplete();
    }
  }, [localData, steps, onComplete, onIncomplete]);

  const handleStepDataChange = (data: any) => {
    const currentCategory = steps[currentSubStep].id;

    setLocalData((prev) => ({
      ...prev,
      [currentCategory]: data,
    }));
    onDataChange(currentCategory, data);
  };

  const getStepComponent = (stepId: string) => {
    // For mixed companies, this function should not be used - use getSectionStepComponent instead
    // This is only for single activity companies
    const stepData = localData[stepId];

    if (stepId === "residue") {
      return (
        <ResidueStep existingData={stepData as ResiduePayload} onDataChange={handleStepDataChange} showTitle={false} />
      );
    }

    if (stepId === "materials") {
      return (
        <MaterialsStep
          existingData={stepData as MaterialPayload}
          onDataChange={handleStepDataChange}
          showTitle={false}
        />
      );
    }

    if (stepId === "electricity") {
      return (
        <ElectricityConsumptionStep
          existingData={stepData as ElectricityPayload}
          onDataChange={handleStepDataChange}
          showTitle={false}
        />
      );
    }

    if (stepId === "fuels") {
      return <FuelsStep existingData={stepData as FuelPayload} onDataChange={handleStepDataChange} showTitle={false} />;
    }

    if (stepId === "water") {
      return (
        <WaterStep existingData={stepData as WaterPayload} onDataChange={handleStepDataChange} showTitle={false} />
      );
    }
  };

  const handleBackClick = () => {
    if (currentSubStep > 0) onSubStepChange(currentSubStep - 1);
  };

  const handleNext = () => {
    if (currentSubStep === steps.length - 1) {
      // Navigate to next main step (General Data)
      if (onNavigateToNextMainStep) {
        onNavigateToNextMainStep();
      }
    } else {
      onSubStepChange(currentSubStep + 1);
    }
  };

  // Helper for rendering a step for a section (extraction or transformation)
  const getSectionStepComponent = (section: "extraction" | "transformation", stepId: string) => {
    const sectionData = section === "extraction" ? extractionData : transformationData;
    const setSectionData = section === "extraction" ? setExtractionData : setTransformationData;
    const handleSectionDataChange = (data: any) => {
      const updatedSectionData = { ...sectionData, [stepId]: data };
      setSectionData(updatedSectionData);

      // Save to the main 'annualConsumptions' object with nested keys
      const currentAnnualConsumptions = formData.annualConsumptions || {};
      onDataChange("annualConsumptions", {
        ...currentAnnualConsumptions,
        [section]: updatedSectionData,
      });
    };

    const stepData = sectionData[stepId];

    if (stepId === "residue") {
      return (
        <ResidueStep
          existingData={stepData as ResiduePayload}
          onDataChange={handleSectionDataChange}
          showTitle={false}
        />
      );
    }
    if (stepId === "materials") {
      return (
        <MaterialsStep
          existingData={stepData as MaterialPayload}
          onDataChange={handleSectionDataChange}
          showTitle={false}
        />
      );
    }
    if (stepId === "electricity") {
      return (
        <ElectricityConsumptionStep
          existingData={stepData as ElectricityPayload}
          onDataChange={handleSectionDataChange}
          showTitle={false}
          checkboxId={`noElectricityConsumption-${section}`}
        />
      );
    }
    if (stepId === "fuels") {
      return (
        <FuelsStep
          existingData={stepData as FuelPayload}
          onDataChange={handleSectionDataChange}
          showTitle={false}
          checkboxId={`noFuelConsumption-${section}`}
        />
      );
    }
    if (stepId === "water") {
      return (
        <WaterStep 
          existingData={stepData as WaterPayload} 
          onDataChange={handleSectionDataChange} 
          showTitle={false} 
          activityType={section}
        />
      );
    }
    return null;
  };

  // Main render
  if (companyType === "extraction_and_transformation") {
    const currentStepId = steps[currentSubStep].id;
    return (
      <div className="w-full max-w-5xl mx-auto">
        <ConfidentialityDisclaimer />
        <div className="bg-white rounded-xl shadow-md border border-gray-100">
          <div className="px-6 pt-5 pb-2">
            <Stepper
              steps={steps}
              activeStep={currentSubStep}
              onStepChange={(stepIndex) => {
                // Allow navigation to any step in sub-stepper
                if (stepIndex >= 0 && stepIndex < steps.length) {
                  onSubStepChange(stepIndex);
                }
              }}
            >
              <></>
            </Stepper>
          </div>
          <div className="p-6 pt-4 flex flex-col gap-8">
            {/* Extraction Section */}
            <div>
              <h3 className="text-base font-semibold text-teal-700 mb-2">
                {t("footprints.annualConsumptions.extractionSection")}
              </h3>
              {getSectionStepComponent("extraction", currentStepId)}
            </div>
            {/* Transformation Section */}
            <div>
              <h3 className="text-base font-semibold text-teal-700 mb-2">
                {t("footprints.annualConsumptions.transformationSection")}
              </h3>
              {getSectionStepComponent("transformation", currentStepId)}
            </div>
          </div>
          {/* Navigation buttons (reuse your existing ones) */}
          <div className="flex items-center justify-between px-6 pb-6">
            <div>
              {currentSubStep > 0 && (
                <Button
                  variant="outline"
                  className="border-teal-200 text-teal-700 hover:bg-teal-50"
                  onClick={handleBackClick}
                >
                  <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />
                  {t("common.back")}
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {currentSubStep < steps.length - 1 ? (
                <Button className="bg-teal-600 text-white hover:bg-teal-700" onClick={handleNext}>
                  {t("common.advanceTo")} {steps[currentSubStep + 1].title}
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              ) : (
                <Button className="bg-teal-600 text-white hover:bg-teal-700" onClick={handleNext}>
                  {t("footprints.consumption.continueToGeneralData")}
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              )}
            </div>
          </div>
          {hasUnsavedChanges && onSaveDraft && (
            <div className="mt-6 pt-4 border-t border-gray-200 px-6 pb-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <AlertCircle className="h-4 w-4" />
                    <span>{t("footprints.consumption.unsavedChanges")}</span>
                  </div>
                  {lastSaved && (
                    <div className="text-xs text-gray-500">
                      {t("footprints.consumption.lastSaved")}:{" "}
                      {(() => {
                        // Convert Firestore timestamp to Date if needed
                        let date = lastSaved;
                        if (lastSaved && typeof lastSaved === "object" && "seconds" in lastSaved) {
                          date = new Date((lastSaved as { seconds: number }).seconds * 1000);
                        }
                        return date instanceof Date
                          ? date.toLocaleString("pt-PT")
                          : t("footprints.consumption.invalidDate");
                      })()}
                    </div>
                  )}
                </div>
                <Button
                  onClick={onSaveDraft}
                  disabled={isSavingDraft}
                  variant="outline"
                  size="sm"
                  className="border-teal-200 text-teal-600 hover:bg-teal-50"
                >
                  {isSavingDraft ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      {t("footprints.consumption.saving")}
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      {t("footprints.consumption.saveDraft")}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto">
      <ConfidentialityDisclaimer />
      <div className="bg-white rounded-xl shadow-md border border-gray-100">
        <div className="px-6 pt-5 pb-2">
          <Stepper
            steps={steps}
            activeStep={currentSubStep}
            onStepChange={(stepIndex) => {
              // Allow navigation to any step in sub-stepper
              if (stepIndex >= 0 && stepIndex < steps.length) {
                onSubStepChange(stepIndex);
              }
            }}
          >
            <></>
          </Stepper>
        </div>

        <div className="p-6 pt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-teal-700">{steps[currentSubStep].title}</h3>
            <div className="flex items-center text-teal-600 bg-teal-50 px-2.5 py-1 rounded-full border border-teal-200 text-xs font-medium">
              <CalendarIcon className="w-3.5 h-3.5 mr-1.5" />
              <span>{year}</span>
            </div>
          </div>

          <Card className="shadow-sm border border-gray-200 rounded-lg mb-4">
            <CardContent className="pt-4 pb-4">
              <div className="flex justify-end items-center mb-4">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button className="text-gray-400 hover:text-gray-500">
                        <InfoIcon className="w-4 h-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>{t("footprints.consumption.stepInfo")}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <p className="text-gray-600 text-sm mb-4">{t(`footprints.guidelines.${steps[currentSubStep].id}`)}</p>

              {getStepComponent(steps[currentSubStep].id)}
            </CardContent>
          </Card>

          <div className="flex items-center justify-between">
            <div>
              {currentSubStep > 0 && (
                <Button
                  variant="outline"
                  className="border-teal-200 text-teal-700 hover:bg-teal-50"
                  onClick={handleBackClick}
                >
                  <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />
                  {t("common.back")}
                </Button>
              )}
            </div>

            <div className="flex gap-2">
              {/* Navigation buttons */}
              {currentSubStep < steps.length - 1 ? (
                <Button className="bg-teal-600 text-white hover:bg-teal-700" onClick={handleNext}>
                  {t("common.advanceTo")} {steps[currentSubStep + 1].title}
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              ) : (
                <Button className="bg-teal-600 text-white hover:bg-teal-700" onClick={handleNext}>
                  {t("footprints.consumption.continueToGeneralData")}
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Save Draft Button */}
        {hasUnsavedChanges && onSaveDraft && (
          <div className="mt-6 pt-4 border-t border-gray-200 px-6 pb-6">
            <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <AlertCircle className="h-4 w-4" />
                  <span>{t("footprints.consumption.unsavedChanges")}</span>
                </div>
                {lastSaved && (
                  <div className="text-xs text-gray-500">
                    {t("footprints.consumption.lastSaved")}:{" "}
                    {(() => {
                      // Convert Firestore timestamp to Date if needed
                      let date = lastSaved;
                      if (lastSaved && typeof lastSaved === "object" && "seconds" in lastSaved) {
                        date = new Date((lastSaved as { seconds: number }).seconds * 1000);
                      }
                      return date instanceof Date
                        ? date.toLocaleString("pt-PT")
                        : t("footprints.consumption.invalidDate");
                    })()}
                  </div>
                )}
              </div>
              <Button
                onClick={onSaveDraft}
                disabled={isSavingDraft}
                variant="outline"
                size="sm"
                className="border-teal-200 text-teal-600 hover:bg-teal-50"
              >
                {isSavingDraft ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    {t("footprints.consumption.saving")}
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    {t("footprints.consumption.saveDraft")}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
